// ============ Utility Functions ============

const DEG_TO_RAD = Math.PI / 180;
const EARTH_RADIUS_KM = 6371;

function haversineKm(lat1, lon1, lat2, lon2) {
  const dLat = (lat2 - lat1) * DEG_TO_RAD;
  const dLon = (lon2 - lon1) * DEG_TO_RAD;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function bearing(lat1, lon1, lat2, lon2) {
  const dLon = (lon2 - lon1) * DEG_TO_RAD;
  const y = Math.sin(dLon) * Math.cos(lat2 * DEG_TO_RAD);
  const x =
    Math.cos(lat1 * DEG_TO_RAD) * Math.sin(lat2 * DEG_TO_RAD) -
    Math.sin(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) * Math.cos(dLon);
  return (Math.atan2(y, x) * 180) / Math.PI;
}

function angleDiff(a, b) {
  let d = b - a;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return Math.abs(d);
}

// ============ Metrics ============

export function computeMetrics(points) {
  if (!points || points.length === 0) {
    return { pointCount: 0, totalDistKm: 0, avgSpeedKmh: 0, maxSpeedKmh: 0, maxSegDistM: 0, smoothness: 0, totalTimeS: 0 };
  }

  let totalDist = 0;
  let maxSegDist = 0;
  let maxSpeed = 0;
  const bearings = [];
  let totalTime = 0;

  for (let i = 1; i < points.length; i++) {
    const d = haversineKm(points[i - 1].latitude, points[i - 1].longitude, points[i].latitude, points[i].longitude);
    totalDist += d;
    if (d > maxSegDist) maxSegDist = d;

    const t0 = new Date(points[i - 1].timestamp).getTime();
    const t1 = new Date(points[i].timestamp).getTime();
    const dtS = (t1 - t0) / 1000;
    if (dtS > 0) {
      const speedKmh = (d / dtS) * 3600;
      if (speedKmh > maxSpeed) maxSpeed = speedKmh;
    }
    totalTime += Math.max(0, dtS);

    if (i >= 2) {
      const b1 = bearing(points[i - 2].latitude, points[i - 2].longitude, points[i - 1].latitude, points[i - 1].longitude);
      const b2 = bearing(points[i - 1].latitude, points[i - 1].longitude, points[i].latitude, points[i].longitude);
      bearings.push(angleDiff(b1, b2));
    }
  }

  const smoothness = bearings.length > 0 ? bearings.reduce((s, v) => s + v, 0) / bearings.length : 0;
  const avgSpeed = totalTime > 0 ? (totalDist / totalTime) * 3600 : 0;

  return {
    pointCount: points.length,
    totalDistKm: totalDist,
    avgSpeedKmh: avgSpeed,
    maxSpeedKmh: maxSpeed,
    maxSegDistM: maxSegDist * 1000,
    smoothness,
    totalTimeS: totalTime,
  };
}

// ============ Outlier / Preprocessing Algorithms ============

function accuracyThreshold(points, { maxAccuracy }) {
  return points.filter(p => {
    const acc = p.accuracy;
    if (acc === null || acc === undefined) return true;
    return acc <= maxAccuracy;
  });
}

function detourOutlier(points, { maxDetourM }) {
  if (points.length < 3) return points.map(p => ({ ...p }));

  const keep = new Array(points.length).fill(true);
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    const withPt =
      haversineKm(prev.latitude, prev.longitude, curr.latitude, curr.longitude) * 1000 +
      haversineKm(curr.latitude, curr.longitude, next.latitude, next.longitude) * 1000;
    const withoutPt = haversineKm(prev.latitude, prev.longitude, next.latitude, next.longitude) * 1000;
    if (withPt - withoutPt > maxDetourM) keep[i] = false;
  }
  return points.filter((_, i) => keep[i]).map(p => ({ ...p }));
}

function speedGate(points, { maxSpeedKmh }) {
  if (points.length < 2) return points.map(p => ({ ...p }));
  const result = [{ ...points[0] }];

  for (let i = 1; i < points.length; i++) {
    const prev = result[result.length - 1];
    const d = haversineKm(prev.latitude, prev.longitude, points[i].latitude, points[i].longitude);
    const dt = (new Date(points[i].timestamp) - new Date(prev.timestamp)) / 1000;
    const speed = dt > 0 ? (d / dt) * 3600 : 0;
    if (speed <= maxSpeedKmh) {
      result.push({ ...points[i] });
    }
  }
  return result;
}

// ============ Smoothing Algorithms ============

function movingAverage(points, { windowSize }) {
  const w = Math.max(1, Math.floor(windowSize));
  const half = Math.floor(w / 2);
  return points.map((p, i) => {
    const lo = Math.max(0, i - half);
    const hi = Math.min(points.length - 1, i + half);
    let sumLat = 0, sumLon = 0, count = 0;
    for (let j = lo; j <= hi; j++) {
      sumLat += points[j].latitude;
      sumLon += points[j].longitude;
      count++;
    }
    return { ...p, latitude: sumLat / count, longitude: sumLon / count };
  });
}

function exponentialSmoothing(points, { alpha, timeWeighted }) {
  if (points.length === 0) return [];
  const result = [{ ...points[0] }];
  let sLat = points[0].latitude;
  let sLon = points[0].longitude;

  for (let i = 1; i < points.length; i++) {
    let a = alpha;
    if (timeWeighted) {
      const dt = (new Date(points[i].timestamp) - new Date(points[i - 1].timestamp)) / 1000;
      a = 1 - Math.pow(1 - alpha, Math.min(dt, 10));
    }
    sLat = a * points[i].latitude + (1 - a) * sLat;
    sLon = a * points[i].longitude + (1 - a) * sLon;
    result.push({ ...points[i], latitude: sLat, longitude: sLon });
  }
  return result;
}

function accuracyWeighted(points, { windowSize }) {
  const w = Math.max(1, Math.floor(windowSize));
  const half = Math.floor(w / 2);
  return points.map((p, i) => {
    const lo = Math.max(0, i - half);
    const hi = Math.min(points.length - 1, i + half);
    let sumLat = 0, sumLon = 0, sumW = 0;
    for (let j = lo; j <= hi; j++) {
      const acc = points[j].accuracy;
      const weight = (acc && acc > 0) ? 1 / (acc * acc) : 1;
      sumLat += points[j].latitude * weight;
      sumLon += points[j].longitude * weight;
      sumW += weight;
    }
    return { ...p, latitude: sumLat / sumW, longitude: sumLon / sumW };
  });
}

function medianFilter(points, { windowSize }) {
  const w = Math.max(1, Math.floor(windowSize));
  const half = Math.floor(w / 2);
  const median = (arr) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };
  return points.map((p, i) => {
    const lo = Math.max(0, i - half);
    const hi = Math.min(points.length - 1, i + half);
    const lats = [], lons = [];
    for (let j = lo; j <= hi; j++) {
      lats.push(points[j].latitude);
      lons.push(points[j].longitude);
    }
    return { ...p, latitude: median(lats), longitude: median(lons) };
  });
}

function kalman1D(vals, timestamps, Q, defaultR, accuracies) {
  const n = vals.length;
  if (n === 0) return [];

  let x = vals[0];
  let v = 0;
  const initR = accuracies ? (accuracies[0] || defaultR) : defaultR;
  let Pxx = initR, Pxv = 0, Pvv = initR * 4;
  const out = [x];

  for (let i = 1; i < n; i++) {
    const dt = Math.max(0.01, (timestamps[i] - timestamps[i - 1]) / 1000);
    const R = accuracies ? (accuracies[i] || defaultR) : defaultR;

    x += v * dt;
    const newPxx = Pxx + 2 * dt * Pxv + dt * dt * Pvv + Q * dt * dt * dt / 3;
    const newPxv = Pxv + dt * Pvv + Q * dt * dt / 2;
    const newPvv = Pvv + Q * dt;
    Pxx = newPxx; Pxv = newPxv; Pvv = newPvv;

    const S = Pxx + R;
    const Kx = Pxx / S;
    const Kv = Pxv / S;
    const innov = vals[i] - x;
    x += Kx * innov;
    v += Kv * innov;
    const Pxv_old = Pxv;
    Pxx -= Kx * Pxx;
    Pxv -= Kx * Pxv;
    Pvv -= Kv * Pxv_old;

    out.push(x);
  }
  return out;
}

function kalmanFilter(points, { processNoise, measurementNoise, useGpsAccuracy }) {
  if (points.length < 2) return points.map(p => ({ ...p }));

  const timestamps = points.map(p => new Date(p.timestamp).getTime());
  const lats = points.map(p => p.latitude);
  const lons = points.map(p => p.longitude);
  const avgLatRad = (lats.reduce((s, v) => s + v, 0) / lats.length) * DEG_TO_RAD;
  const mPerDegLat = 111320;
  const mPerDegLon = 111320 * Math.cos(avgLatRad);

  const qLat = (processNoise * processNoise) / (mPerDegLat * mPerDegLat);
  const qLon = (processNoise * processNoise) / (mPerDegLon * mPerDegLon);
  const rLatDef = (measurementNoise / mPerDegLat) ** 2;
  const rLonDef = (measurementNoise / mPerDegLon) ** 2;

  let accLat = null, accLon = null;
  if (useGpsAccuracy) {
    accLat = points.map(p => { const a = p.accuracy > 0 ? p.accuracy : measurementNoise; return (a / mPerDegLat) ** 2; });
    accLon = points.map(p => { const a = p.accuracy > 0 ? p.accuracy : measurementNoise; return (a / mPerDegLon) ** 2; });
  }

  const smoothLats = kalman1D(lats, timestamps, qLat, rLatDef, accLat);
  const smoothLons = kalman1D(lons, timestamps, qLon, rLonDef, accLon);

  return points.map((p, i) => ({ ...p, latitude: smoothLats[i], longitude: smoothLons[i] }));
}

// ============ Algorithm Registry ============

export const ALGORITHMS = {
  // --- Outlier Detection / Preprocessing ---
  accuracyThreshold: {
    name: 'Accuracy Threshold',
    category: 'outlier',
    description: 'Removes points where GPS-reported accuracy exceeds threshold.',
    help: [
      'Condition: keep point if accuracy <= maxAccuracy',
      'GPS chips report an estimated horizontal accuracy (meters). High values indicate unreliable fixes (urban canyons, indoor, cold start).',
      'Typical good readings: 3-15m. Values >50m are usually noise.',
      'This is the simplest outlier filter and should usually be the first stage.',
    ],
    params: [
      { key: 'maxAccuracy', label: 'Max Accuracy (m)', type: 'range', min: 5, max: 200, step: 1, default: 50 },
    ],
    fn: accuracyThreshold,
  },
  detourOutlier: {
    name: 'Detour Outlier Removal',
    category: 'outlier',
    description: 'Removes points that create large detours from the path.',
    help: [
      'For each point B in sequence A-B-C:',
      '  detour = dist(A,B) + dist(B,C) - dist(A,C)',
      'If detour > threshold, B is an outlier (a GPS "jump").',
      'This catches spikes where a point teleports away from the true path and back. First and last points are always kept.',
      'Use 10-50m for walking, 50-200m for vehicles.',
    ],
    params: [
      { key: 'maxDetourM', label: 'Max Detour (m)', type: 'range', min: 5, max: 500, step: 5, default: 50 },
    ],
    fn: detourOutlier,
  },
  speedGate: {
    name: 'Speed Gate',
    category: 'outlier',
    description: 'Removes points implying impossible speeds.',
    help: [
      'For each consecutive pair of kept points:',
      '  speed = haversine(prev, curr) / dt',
      'If speed > maxSpeed, the point is dropped.',
      'Uses the last *kept* point as reference, so removing one outlier does not cascade.',
      'Walking: 10-15 km/h. Running: 20-30 km/h. Driving: 100-200 km/h.',
    ],
    params: [
      { key: 'maxSpeedKmh', label: 'Max Speed (km/h)', type: 'range', min: 1, max: 200, step: 1, default: 30 },
    ],
    fn: speedGate,
  },

  // --- Smoothing Filters ---
  movingAverage: {
    name: 'Moving Average',
    category: 'smooth',
    description: 'Averages coordinates within a sliding window. Sample-based.',
    help: [
      'For each point i with window W:',
      '  lat[i] = mean(lat[i-W/2 .. i+W/2])',
      '  lon[i] = mean(lon[i-W/2 .. i+W/2])',
      'Simple and fast. Preserves point count. Larger window = more smoothing but loses sharp turns.',
      'Does not consider time gaps between samples — purely sample-indexed.',
    ],
    params: [
      { key: 'windowSize', label: 'Window Size', type: 'range', min: 3, max: 51, step: 2, default: 5 },
    ],
    fn: movingAverage,
  },
  exponentialSmoothing: {
    name: 'Exponential Smoothing (LPF)',
    category: 'smooth',
    description: 'Low-pass filter. Lower alpha = more smoothing.',
    help: [
      'Recurrence:',
      '  S[i] = alpha * X[i] + (1 - alpha) * S[i-1]',
      'alpha near 0: heavy smoothing (trusts history). alpha near 1: passes raw data through.',
      'Time-weighted mode adapts alpha to sample interval:',
      '  alpha_eff = 1 - (1 - alpha)^dt',
      'This means irregular sample gaps are handled correctly — a 5s gap applies more "decay" than a 1s gap.',
    ],
    params: [
      { key: 'alpha', label: 'Alpha', type: 'range', min: 0.01, max: 1, step: 0.01, default: 0.3 },
      { key: 'timeWeighted', label: 'Adapt to sample rate', type: 'checkbox', default: true },
    ],
    fn: exponentialSmoothing,
  },
  accuracyWeighted: {
    name: 'Accuracy-Weighted Average',
    category: 'smooth',
    description: 'Weights points by 1/accuracy². Better readings contribute more.',
    help: [
      'For each point i with window W:',
      '  w[j] = 1 / accuracy[j]^2',
      '  lat[i] = sum(w[j]*lat[j]) / sum(w[j])',
      'Points with lower GPS accuracy values (= more precise) get higher weight.',
      'Falls back to equal weighting if accuracy data is missing.',
    ],
    params: [
      { key: 'windowSize', label: 'Window Size', type: 'range', min: 3, max: 51, step: 2, default: 7 },
    ],
    fn: accuracyWeighted,
  },
  medianFilter: {
    name: 'Median Filter',
    category: 'smooth',
    description: 'Replaces each point with the median of its window.',
    help: [
      'For each point i:',
      '  lat[i] = median(lat[i-W/2 .. i+W/2])',
      '  lon[i] = median(lon[i-W/2 .. i+W/2])',
      'Unlike mean, median is robust to outliers — one extreme value in the window does not pull the result.',
      'Good for removing isolated spikes without blurring the overall path.',
    ],
    params: [
      { key: 'windowSize', label: 'Window Size', type: 'range', min: 3, max: 21, step: 2, default: 5 },
    ],
    fn: medianFilter,
  },
  kalmanFilter: {
    name: 'Kalman Filter',
    category: 'smooth',
    description: 'State estimator: predicts position from velocity, fuses with GPS measurement.',
    help: [
      'State: [position, velocity] for lat and lon independently.',
      'Predict step (constant velocity):',
      '  x_pred = x + v * dt',
      '  P_pred = F * P * F^T + Q',
      'Update step (fuse GPS measurement):',
      '  K = P_pred / (P_pred + R)',
      '  x = x_pred + K * (measurement - x_pred)',
      'Process Noise Q (m/s²): how much acceleration to expect. Higher = more responsive, lower = smoother.',
      'Measurement Noise R (m): GPS uncertainty. Can use per-point accuracy from the chip.',
      'This is the gold standard for GPS smoothing because it models physics (objects have inertia).',
    ],
    params: [
      { key: 'processNoise', label: 'Process Noise (m/s²)', type: 'range', min: 0.1, max: 20, step: 0.1, default: 2.0 },
      { key: 'measurementNoise', label: 'Measurement Noise (m)', type: 'range', min: 1, max: 100, step: 1, default: 10 },
      { key: 'useGpsAccuracy', label: 'Use per-point GPS accuracy', type: 'checkbox', default: true },
    ],
    fn: kalmanFilter,
  },
};

export const ALGO_KEYS_OUTLIER = Object.keys(ALGORITHMS).filter(k => ALGORITHMS[k].category === 'outlier');
export const ALGO_KEYS_SMOOTH = Object.keys(ALGORITHMS).filter(k => ALGORITHMS[k].category === 'smooth');
export const ALGO_KEYS = Object.keys(ALGORITHMS);

export function runPipeline(points, pipeline) {
  let current = points.map(p => ({ ...p }));
  for (const stage of pipeline) {
    const algo = ALGORITHMS[stage.key];
    if (algo && current.length > 0) {
      current = algo.fn(current, stage.params);
    }
  }
  return current;
}
